/**
 * StatusChipsRow
 * Horizontal row of status badges: Overdue, Blocked, Effort estimate, AI Confidence
 * Right-aligned "Why AI picked this" link
 */

import { makeStyles, tokens, Badge, Link } from '@fluentui/react-components'
import { ClockRegular, LockClosedRegular, ArrowRightRegular } from '@fluentui/react-icons'
import type { IssueDetails, EffortEstimate } from '../../../domain'
import { ConfidenceChip } from './ConfidenceChip'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    flex: 1,
  },
  overdueBadge: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  blockedBadge: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  effortBadge: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  badgeIcon: {
    fontSize: '12px',
    marginRight: tokens.spacingHorizontalXXS,
  },
  whyLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
  arrowIcon: {
    fontSize: '12px',
  },
})

const effortLabels: Record<EffortEstimate, string> = {
  quick: 'Quick fix',
  moderate: 'Moderate effort',
  extended: 'Extended effort',
}

export interface StatusChipsRowProps {
  issue: IssueDetails
  onWhyAIPicked?: () => void
}

export function StatusChipsRow({ issue, onWhyAIPicked }: StatusChipsRowProps) {
  const styles = useStyles()

  const isOverdue = issue.tags.some((tag) => tag.type === 'OVERDUE')
  const isBlocked = issue.tags.some((tag) => tag.type === 'BLOCKED')

  return (
    <div className={styles.root}>
      <div className={styles.chips}>
        {isOverdue && (
          <Badge appearance="tint" size="small" className={styles.overdueBadge}>
            <ClockRegular className={styles.badgeIcon} />
            Overdue
          </Badge>
        )}
        {isBlocked && (
          <Badge appearance="tint" size="small" className={styles.blockedBadge}>
            <LockClosedRegular className={styles.badgeIcon} />
            Blocked{issue.blockedDuration ? ` ${issue.blockedDuration}` : ''}
          </Badge>
        )}
        {issue.effortEstimate && (
          <Badge appearance="tint" size="small" className={styles.effortBadge}>
            {effortLabels[issue.effortEstimate]}
          </Badge>
        )}
        {issue.aiConfidence && (
          <ConfidenceChip confidence={issue.aiConfidence} signals={issue.confidenceSignals} />
        )}
      </div>
      <Link
        className={styles.whyLink}
        href="#"
        onClick={(e) => {
          e.preventDefault()
          onWhyAIPicked?.()
        }}
      >
        Why AI picked this
        <ArrowRightRegular className={styles.arrowIcon} />
      </Link>
    </div>
  )
}
